import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  IWorkorderResponse,
  LinkVehicleToWorkorderRejection,
  LinkVehicleToWorkorderResolvingAction,
  LinkVehicleToWorkorderResolvingActionType,
  WorkorderStatus
} from '@cbgms/api'
import { List, ListItem, Typography } from '@mui/material'
import { ConfirmDialog } from '@carsys/ui/src'
import { EmptyDate, formatters } from '@cbgms/base/utils/datetime'

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (actions: LinkVehicleToWorkorderResolvingAction[]) => void
  rejections: LinkVehicleToWorkorderRejection[]
}

const ConfirmModalLinkVehicle: React.FC<Props> = ({ rejections, open, onClose, onConfirm }) => {
  const { t } = useTranslation()

  const getWorkOrderStatusString = (status: WorkorderStatus) =>
    status === WorkorderStatus.Quotation ? t('workorder.status_pending') : t('workorder.status_approved')

  const getDateString = (date: string, time?: string) => {
    const dateMoment = formatters.datetime.fromApiFormat(`${date} ${time}`)
    return formatters.datetime.toUserFormat(dateMoment)
  }

  if (
    rejections.length < 1 ||
    rejections[0]?.ResolvingActions.length < 1 ||
    rejections[0].ResolvingActions[0]?.ActionType !== LinkVehicleToWorkorderResolvingActionType.IgnoreRelatedWorkorders
  ) {
    return null
  }

  // for now, only one rejection is expected: related_workorders
  const relatedWorkorders = rejections[0].Data as IWorkorderResponse[]

  return (
    <ConfirmDialog
      open={open}
      title={t('workorder.link_vehicle_modal.title')}
      content={
        <>
          <Typography variant='h6'>{t('link_vehicle_to_workorder.confirm_modal_link_vehicle.related_workorders_text')}</Typography>
          <List>
            {relatedWorkorders.map(wo => (
              <ListItem key={wo.Workorder.UUID}>
                <Link to={`/workorders/${wo.Workorder.UUID}`} data-testid='related-wo-link'>
                  <strong>{wo.Workorder.ID}</strong> | {getWorkOrderStatusString(wo.Workorder.Status)}
                  {wo.Appointment?.Date && wo.Appointment.Date !== EmptyDate && (
                    <span>
                      {' |'} {t('link_vehicle_to_workorder.confirm_modal_link_vehicle.appointment_on')}{' '}
                      {getDateString(wo.Appointment.Date, wo.Appointment.StartingTime!)}
                    </span>
                  )}
                </Link>
              </ListItem>
            ))}
          </List>
        </>
      }
      cancelButtonContent={t('global.cancel')}
      confirmButtonContent={t('global.continue')}
      onClose={onClose}
      onConfirm={() => onConfirm(rejections[0].ResolvingActions)}
    />
  )
}

export default ConfirmModalLinkVehicle
